import React, { useEffect, useState } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import CountUp from "react-countup";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale,
  PointElement, LineElement,
  BarElement, ArcElement,
  Title, Tooltip, Legend
} from "chart.js";
import "./Dashboard.css";

ChartJS.register(
  CategoryScale, LinearScale,
  PointElement, LineElement,
  BarElement, ArcElement,
  Title, Tooltip, Legend
);

const STATUS_COLORS = {
  Pending:   "#f59e0b",
  Approved:  "#10b981",
  Completed: "#3b82f6",
  Cancelled: "#ef4444",
  New:       "#8b5cf6",
};
const FALLBACK_COLORS = ["#6366f1","#ec4899","#14b8a6","#f97316","#84cc16"];

function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]     = useState(true);

  // Detect dark mode
  const isDark = document.documentElement.classList.contains('dark');

  useEffect(() => {
    fetch("https://api.kemari-corp.com/analytics.php")
      .then(res => res.json())
      .then(data => { if (data.status === "success") setAnalytics(data.data); })
      .catch(err => console.error("Failed to fetch analytics:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="dashboard-loading">Loading analytics...</div>;
  if (!analytics) return <div className="dashboard-loading">Failed to load data.</div>;

  const { total_requests, daily_requests, by_status, by_service } = analytics;

  const thisMonth = daily_requests.reduce((sum, d) => sum + parseInt(d.count), 0);

  // Theme colors
  const textColor = isDark ? '#fff' : '#000';
  const gridColor = isDark ? '#1a1a1a' : '#e5e5e5';
  const accentColor = '#1d9bf0';

  // ── LINE CHART DATA ──
  const lineData = {
    labels: daily_requests.map(d =>
      new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    ),
    datasets: [{
      label: "Requests",
      data: daily_requests.map(d => parseInt(d.count)),
      borderColor: accentColor,
      backgroundColor: isDark 
        ? "rgba(29, 155, 240, 0.1)" 
        : "rgba(29, 155, 240, 0.05)",
      borderWidth: 2,
      pointRadius: 3,
      pointBackgroundColor: accentColor,
      pointBorderColor: isDark ? '#000' : '#fff',
      pointBorderWidth: 2,
      tension: 0.4,
      fill: true,
    }]
  };

  // ── PIE CHART DATA ──
  const pieData = {
    labels: by_status.map(s => s.status),
    datasets: [{
      data: by_status.map(s => parseInt(s.count)),
      backgroundColor: by_status.map((s, i) =>
        STATUS_COLORS[s.status] || FALLBACK_COLORS[i % FALLBACK_COLORS.length]
      ),
      borderWidth: 2,
      borderColor: isDark ? '#0a0a0a' : '#fff',
    }]
  };

  // ── BAR CHART DATA ──
  const barData = {
    labels: by_service.map(s => s.service),
    datasets: [{
      label: "Requests",
      data: by_service.map(s => parseInt(s.count)),
      backgroundColor: accentColor,
      borderRadius: 4,
      borderWidth: 0,
    }]
  };

  const chartOptions = (type) => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: "bottom",
        labels: {
          color: textColor,
          font: {
            family: '"DM Sans", -apple-system, "Helvetica Neue", sans-serif',
            size: 11,
            weight: '400',
          },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
        }
      },
      title: { display: false },
      tooltip: { 
        mode: "index", 
        intersect: false,
        backgroundColor: isDark ? '#1a1a1a' : '#fff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        titleFont: {
          family: '"DM Sans", -apple-system, "Helvetica Neue", sans-serif',
          size: 12,
          weight: '400',
        },
        bodyFont: {
          family: '"DM Sans", -apple-system, "Helvetica Neue", sans-serif',
          size: 11,
          weight: '300',
        },
      }
    },
    scales: type !== "pie" ? {
      x: {
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        ticks: {
          color: isDark ? '#888' : '#666',
          font: {
            family: '"DM Sans", -apple-system, "Helvetica Neue", sans-serif',
            size: 11,
            weight: '300',
          },
        },
        border: {
          display: false,
        },
      },
      y: { 
        beginAtZero: true, 
        ticks: { 
          stepSize: 1,
          color: isDark ? '#888' : '#666',
          font: {
            family: '"DM Sans", -apple-system, "Helvetica Neue", sans-serif',
            size: 11,
            weight: '300',
          },
        },
        grid: {
          color: gridColor,
          drawBorder: false,
        },
        border: {
          display: false,
        },
      }
    } : undefined
  });

  return (
    <div className="dashboard-page">
      <h2>Dashboard</h2>

      {/* ── CARDS ── */}
      <div className="dashboard-cards">
        <div className="dash-card">
          <span className="dash-card-label">Total Requests</span>
          <span className="dash-card-value">
            <CountUp end={total_requests} duration={1.5} />
          </span>
        </div>
        <div className="dash-card">
          <span className="dash-card-label">This Month</span>
          <span className="dash-card-value">
            <CountUp end={thisMonth} duration={1.5} />
          </span>
        </div>
        <div className="dash-card">
          <span className="dash-card-label">Services Offered</span>
          <span className="dash-card-value">
            <CountUp end={by_service.length} duration={1.5} />
          </span>
        </div>
      </div>

      {/* ── LINE CHART ── */}
      <div className="chart-card">
        <h3>Daily Requests (Last 30 Days)</h3>
        <Line data={lineData} options={chartOptions("line")} height={80} />
      </div>

      {/* ── PIE + BAR ── */}
      <div className="charts-row">
        <div className="chart-card">
          <h3>Requests by Status</h3>
          <Pie data={pieData} options={chartOptions("pie")} />
        </div>
        <div className="chart-card">
          <h3>Requests by Service</h3>
          <Bar data={barData} options={chartOptions("bar")} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;